import {Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';
import {BaseComponent} from "../../../../BaseComponent";
import {DataUnit} from "../../../../../models/data-unit/DataUnit";
import {ClickEvent} from "devextreme/ui/button";
import {DelDataUnitCB} from "../../../../../ope/data-unit/DelDataUnitCB";
import {GetDataUnitsCB} from "../../../../../ope/data-unit/GetDataUnitsCB";
import {DeviceTypeOpeService} from "../../../../../services/ope/deviceTypeOpe.service";
import {Metric, MetricGroup} from "../../../../../models/device-type/DeviceType";
import {DataType} from "../../../../../models/metric/DataType";
import {DxDataGridComponent} from "devextreme-angular";
import {RowStateTracker} from "../../../../../utils/RowStateTracker";
import {focusEditRow} from "../../../../../utils/focusEditRow";
import * as uuid from "uuid";

@Component({
  selector: 'app-base-protocol-form',
  templateUrl: './base-protocol-form.component.html',
  styleUrls: ['./base-protocol-form.component.css']
})
export abstract class BaseProtocolFormComponent extends BaseComponent {
  @ViewChild('fileInput') fileInput!: ElementRef;

  // Each subclass binds this to its own grid's template ref (#editMetricDataGrid / #groupDataGrid).
  abstract dataGrid?: DxDataGridComponent;

  // A metric table's tracker is owned by the parent, which hands it to the group table too.
  @Input() rowStateTracker = new RowStateTracker();

  // Group tables only: the metric table's tracker, scoped by group id. Drives each group row's dot
  // and blocks changing the selected group while a metric is being edited.
  @Input() metricRowStateTracker?: RowStateTracker;
  @Output() metricEditBlocked: EventEmitter<void> = new EventEmitter();

  @Output() checkEditDeviceTypeValidation: EventEmitter<any> = new EventEmitter();

  deviceTypeOpeService: DeviceTypeOpeService = inject(DeviceTypeOpeService);

  // Called by the parent before a device-type save, if a row is still "in modifica" here.
  cancelActiveEdit(): void {
    this.dataGrid?.instance.cancelEditData();
  }

  focusActiveEdit(): void {
    focusEditRow(this.dataGrid);
  }

  // Changing the selected group swaps the metric grid's rows, which would drop the metric being edited.
  protected blockedByMetricEdit(): boolean {
    if (!this.metricRowStateTracker?.hasEditing()) return false;
    this.metricEditBlocked.emit();
    return true;
  }

  private _dataUnitValues: DataUnit[] = [];
  private _groupIsBeingEdited: boolean = false;
  private _groupMetric?: MetricGroup;

  protected PROTOCOL: string = "";

  @Output() groupIsBeingEditedChange: EventEmitter<boolean> = new EventEmitter();

  get groupIsBeingEdited(): boolean {
    return this._groupIsBeingEdited;
  }

  @Input() set groupIsBeingEdited(value: boolean) {
    this._groupIsBeingEdited = value;
    this.groupIsBeingEditedChange.emit(this.groupIsBeingEdited);
  }

  @Output() dataUnitValuesChange: EventEmitter<any> = new EventEmitter();

  get dataUnitValues(): DataUnit[] {
    return this._dataUnitValues;
  }

  @Input() set dataUnitValues(value: DataUnit[]) {
    this._dataUnitValues = value;
    this.dataUnitValuesChange.emit(this.dataUnitValues)
  }

  @Output() groupMetricChange: EventEmitter<any> = new EventEmitter();

  get groupMetric(): any {
    return this._groupMetric;
  }

  @Input() set groupMetric(value: any) {
    this._groupMetric = value;
    this.groupMetricChange.emit(this.groupMetric);
  }


  constructor() {
    super();
  }

  ngOnInit() {
    super.ngOnInit();
    this.getDataUnits();
  }

  /**
   * Get all Data Units for metrics
   */
  getDataUnits() {
    const cb = new GetDataUnitsCB();

    this.deviceTypeOpeService.getDataUnits(cb).then((_) => {

      this._dataUnitValues = cb.o.metricInstances;

    });
  }

  onCellPreparedMetric(e: any) {
    if (e.rowType === 'data' && e.column.command === 'edit') {
      this.onCellPreparedSwapSaveIcon(e);
    }
  }

  onCellPreparedMetricGroup(e: any) {
    if (!!e && e.rowType === 'data' && e.column.command === 'edit' && !!e.data) {
      let currentGroup = e.data as MetricGroup;
      if (currentGroup.metrics) {
        this.onCellPreparedSwapSaveIcon(e);
      }
    }
  }


  onCellPreparedSwapSaveIcon(e: any) {

    if (e.rowType === 'data' && e.column.command === 'edit') {
      const saveButton = e.cellElement.querySelector('.dx-link-save');
      if (saveButton) {
        saveButton.classList.remove('dx-icon-save');
        saveButton.classList.add('dx-icon-check');
      }
    }
  }

  putDefaultMetricValues(event: any) {
    event.data.transportConfiguration = {...event.data.transportConfiguration, byteOrder: this.gmService.defaultByteOrderValue?.value};
    event.data.divider = 1;
    event.data.dataUnit = undefined;
    event.data.decimalDigits = 2;
  }


  async deleteDataUnit(dataUnit: DataUnit, $event: ClickEvent) {
    $event.event?.stopPropagation();
    let result = this.showDeleteDialog(dataUnit.name).show().done();
    await result.then(async (dialogResult: any) => {
      if (dialogResult) {
        if (dataUnit.id < 0) {
          this._dataUnitValues = this._dataUnitValues.filter(d => d.id !== dataUnit.id);
          this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.DEVICE_TYPE.TEXT.DATA_UNIT_DELETED"));
        } else {
          const cb = new DelDataUnitCB();
          cb.i.id = dataUnit.id;
          this.deviceTypeOpeService.delDataUnit(cb).then((_) => {
            this.toastService.openSuccessToastMessage(this.translate.instant("REGISTRY.DEVICE_TYPE.TEXT.DATA_UNIT_DELETED"));
            this.getDataUnits();
          });
        }
      }
    });
  }


  cleanUpCsvData(newData: Metric[], dataTypeValues: DataType[]) {
    for (let metric of newData) {
      const fullDataType = this.getFullDataType(metric.transportConfiguration.dataType);
      if (!fullDataType?.supportsBitPosition) {
        metric.transportConfiguration.bitPosition = null;
      }
      if (!fullDataType?.supportsDecimals) {
        metric.decimalDigits = undefined;
      }
      if (!fullDataType?.supportsUnit) {
        metric.dataUnit = undefined;
      }
      if (!fullDataType?.supportsDivisor) {
        metric.divider = null;
      }
    }
  }

  // The tracker is shared by every group this table shows, so only the replaced group's keys are
  // dropped. Imported rows carry fresh ids and are saved as new metrics, hence all marked new.
  protected markCsvImportedRows(replaced: Metric[] | undefined, imported: Metric[]) {
    for (const metric of replaced ?? [])
      this.rowStateTracker.reset((metric as any).id);
    for (const metric of imported)
      this.rowStateTracker.markNew((metric as any).id, this.groupMetric?.id);
  }

  protected abstract getFullDataType(dataTypeName: string): DataType | undefined;

  protected duplicateName(name: string | undefined, existingNames: (string | undefined)[] = []): string {
    const original = name ?? '';
    const base = original.match(/^(.*) copia \d+$/)?.[1] ?? original;

    const usedNumbers = existingNames
      .map(n => n?.match(new RegExp(`^${this.escapeRegExp(base)} copia (\\d+)$`))?.[1])
      .filter((n): n is string => !!n)
      .map(Number);

    const nextNumber = usedNumbers.length ? Math.max(...usedNumbers) + 1 : 1;
    return `${base} copia ${nextNumber}`;
  }

  private escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  protected cloneMetric(metric: Metric, existingNames: (string | undefined)[] = []): Metric {
    const clone: Metric = JSON.parse(JSON.stringify(metric));
    (clone as any).id = 'a' + uuid.v4();
    clone.name = this.duplicateName(metric.name, existingNames);
    return clone;
  }

  protected cloneGroup(group: MetricGroup, existingNames: (string | undefined)[] = []): MetricGroup {
    const clone: MetricGroup = JSON.parse(JSON.stringify(group));
    (clone as any).id = 'a' + uuid.v4();
    clone.name = this.duplicateName(group.name, existingNames);
    clone.metrics = (group.metrics ?? []).map(m => this.cloneMetric(m, (group.metrics ?? []).map(mm => mm.name)));
    return clone;
  }

}
